import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import WgptBoardsAPI from '../../api/wgpt/boards';
import types from '../mutation-types';
import { throwErrorMessage } from '../utils/api';

const state = {
  records: [],
  automationRecords: [],
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isFetchingAutomations: false,
    isCreating: false,
    isDeleting: false,
    isUpdating: false,
    isSearching: false,
  },
};

export const getters = {
  getBoards($state) {
    return $state.records;
  },
  getBoard: $state => boardId => {
    const [board] = $state.records.filter(
      record => record.id === Number(boardId)
    );
    return board || {};
  },
  getBoardAutomations: $state => {
    return $state.automationRecords;
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
};

export const actions = {
  get: async ({ commit }) => {
    commit(types.SET_WGPT_BOARD_UI_FLAG, { isFetching: true });
    try {
      const response = await WgptBoardsAPI.get();
      commit(types.SET_WGPT_BOARDS, response.data);
      return response.data;
    } catch (error) {
      // Ignore error
      return undefined;
    } finally {
      commit(types.SET_WGPT_BOARD_UI_FLAG, { isFetching: false });
    }
  },
  create: async ({ commit }, boardObj) => {
    commit(types.SET_WGPT_BOARD_UI_FLAG, { isCreating: true });
    try {
      const response = await WgptBoardsAPI.create(boardObj);
      commit(types.ADD_WGPT_BOARD, response.data);
      return response.data;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_BOARD_UI_FLAG, { isCreating: false });
    }
  },
  update: async ({ commit }, { id, ...updateObj }) => {
    commit(types.SET_WGPT_BOARD_UI_FLAG, { isUpdating: true });
    try {
      const response = await WgptBoardsAPI.update(id, updateObj);
      commit(types.EDIT_WGPT_BOARD, response.data);
      return response.data;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_BOARD_UI_FLAG, { isUpdating: false });
    }
  },
  delete: async ({ commit }, id) => {
    commit(types.SET_WGPT_BOARD_UI_FLAG, { isDeleting: true });
    try {
      await WgptBoardsAPI.delete(id);
      commit(types.DELETE_WGPT_BOARD, id);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_BOARD_UI_FLAG, { isDeleting: false });
    }
  },
  show: async ({ commit }, id) => {
    commit(types.SET_WGPT_BOARD_UI_FLAG, { isFetchingItem: true });
    try {
      const { data } = await WgptBoardsAPI.show(id);
      commit(types.ADD_WGPT_BOARD, data);
      return data;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_BOARD_UI_FLAG, { isFetchingItem: false });
    }
  },
  addBoard({ commit }, board) {
    commit(types.ADD_WGPT_BOARD, board);
  },
  updateBoard({ commit }, board) {
    commit(types.EDIT_WGPT_BOARD, board);
  },
  deleteBoard({ commit }, { id }) {
    commit(types.DELETE_WGPT_BOARD, id);
  },
  getAutomations: async ({ commit }, id) => {
    commit(types.SET_WGPT_BOARD_AUTOMATIONS, []);
    commit(types.SET_WGPT_BOARD_UI_FLAG, { isFetchingAutomations: true });
    try {
      const response = await WgptBoardsAPI.getAutomations(id);
      commit(types.SET_WGPT_BOARD_AUTOMATIONS, response.data);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_BOARD_UI_FLAG, { isFetchingAutomations: false });
    }
  },
};

// Only the boards list sends a board's stages (lists): a board from anywhere else keeps the ones it had
const keepingLists = mutation => ($state, data) => {
  const record = $state.records.find(({ id }) => id === data.id);
  mutation(
    $state,
    data.lists || !record?.lists ? data : { ...data, lists: record.lists }
  );
};

export const mutations = {
  [types.SET_WGPT_BOARD_UI_FLAG]($state, data) {
    $state.uiFlags = { ...$state.uiFlags, ...data };
  },
  [types.ADD_WGPT_BOARD]: keepingLists(MutationHelpers.setSingleRecord),
  [types.SET_WGPT_BOARDS]: MutationHelpers.set,
  [types.EDIT_WGPT_BOARD]: keepingLists(MutationHelpers.update),
  [types.DELETE_WGPT_BOARD]: MutationHelpers.destroy,
  [types.SET_WGPT_BOARD_AUTOMATIONS]($state, data) {
    $state.automationRecords = data;
  },
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
